import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Camera, Upload, ScanLine, Loader2, Ruler, Sparkles, Wand2, CheckCircle2,
  RefreshCw, Save, ShoppingBag, ExternalLink, ShieldCheck, ArrowRight, Shirt, Share2, Repeat,
  MessageCircle, Instagram, Link2, Download,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ANGLES = [
  { key: "front", label: "Frontale", hint: "Tutta la figura, di fronte" },
  { key: "side", label: "Laterale", hint: "Di profilo" },
  { key: "back", label: "Posteriore", hint: "Di spalle" },
];

const STYLES = [
  "Elegante", "Classico", "Sportivo", "Estivo", "Invernale",
  "Primaverile", "Lavoro / Business", "Costumi da Bagno / Resort",
];

const COLORS = ["Neutri caldi", "Monocromo Noir", "Pastello soft", "Colori decisi", "Libera"];

function PhotoCard({ angle, uploaded, preview, onSelect }) {
  const inputRef = useRef(null);
  return (
    <div
      data-testid={`photo-upload-${angle.key}`}
      onClick={() => inputRef.current?.click()}
      className={`relative cursor-pointer rounded-lg border transition-colors overflow-hidden h-64 flex flex-col items-center justify-center ${
        uploaded ? "border-[#2f74f0]/60 bg-white/85" : "border-dashed border-[#1e5fe0]/30 bg-white/70 hover:border-[#2f74f0]/50"
      }`}
    >
      {preview ? (
        <img src={preview} alt={angle.label} className="absolute inset-0 h-full w-full object-cover opacity-90" />
      ) : (
        <div className="text-center px-4">
          <Camera className="h-8 w-8 text-[#a9772f] mx-auto mb-3" />
          <div className="font-serif text-lg text-[#2e2015]">{angle.label}</div>
          <div className="text-xs text-[#8a7760] mt-1">{angle.hint}</div>
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#2f74f0]"><Upload className="h-3.5 w-3.5" /> Carica foto</div>
        </div>
      )}
      {uploaded && (
        <div className="absolute top-2 right-2 bg-emerald-500/90 rounded-full p-1">
          <CheckCircle2 className="h-4 w-4 text-white" />
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#dce6ed]/90 to-transparent px-3 py-2">
        <span className="text-xs font-mono uppercase tracking-widest text-[#1e5fe0]">{angle.label}</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onSelect(angle.key, e.target.files[0])}
      />
    </div>
  );
}

export default function Studio() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [profile, setProfile] = useState(user?.body_profile || null);

  const [style, setStyle] = useState("Elegante");
  const [budget, setBudget] = useState(500);
  const [color, setColor] = useState("Libera");
  const [occasion, setOccasion] = useState("");
  const [generating, setGenerating] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [saved, setSaved] = useState(false);
  const [variantCount, setVariantCount] = useState(1);
  const [variants, setVariants] = useState([]);
  const [swapping, setSwapping] = useState(null);

  useEffect(() => {
    api.get("/profile").then((r) => r.data.body_profile && setProfile(r.data.body_profile)).catch(() => {});
  }, []);

  const selectPhoto = async (angleKey, file) => {
    const previewUrl = URL.createObjectURL(file);
    setPhotos((p) => ({ ...p, [angleKey]: { preview: previewUrl, uploading: true } }));
    const form = new FormData();
    form.append("angle", angleKey);
    form.append("file", file);
    try {
      await api.post("/photos/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => ({ ...p, [angleKey]: { preview: previewUrl, uploading: false, uploaded: true } }));
      toast.success(`Foto ${angleKey} caricata`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPhotos((p) => ({ ...p, [angleKey]: undefined }));
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post("/analyze");
      setProfile(res.data);
      await refreshUser();
      toast.success("Analisi completata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAnalyzing(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setProposal(null);
    setSaved(false);
    try {
      const res = await api.post("/outfits/generate", {
        style, budget, occasion, color_preference: color,
      });
      setProposal(res.data);
      toast.success("Outfit generato");
      setTimeout(() => document.getElementById("proposal-anchor")?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error("Crediti esauriti. Passa alla schermata Crediti per ricaricare.", {
          action: { label: "Vai ai Crediti", onClick: () => navigate("/credits") },
        });
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setGenerating(false);
    }
  };

  const generateVariants = async () => {
    setGenerating(true);
    setProposal(null);
    setVariants([]);
    setSaved(false);
    try {
      const res = await api.post("/outfits/generate-variants", {
        style, budget, occasion, color_preference: color, count: variantCount,
      });
      setVariants(res.data.proposals || []);
      toast.success(`${res.data.generated} varianti generate`);
      setTimeout(() => document.getElementById("proposal-anchor")?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail), {
          action: { label: "Vai ai Crediti", onClick: () => navigate("/credits") },
        });
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = () => (variantCount > 1 ? generateVariants() : generate());

  const chooseVariant = (v) => {
    setVariants([]);
    setProposal(v);
    setSaved(false);
    setTimeout(() => document.getElementById("proposal-anchor")?.scrollIntoView({ behavior: "smooth" }), 150);
  };

  const shareProposal = async (channel = "copy") => {
    try {
      const r = await api.post(`/outfits/${proposal.id}/share`);
      const url = `${window.location.origin}/api/share/${r.data.share_id}`;
      const text = `Guarda questo look "${proposal.title}" su My Look`;
      if (channel === "whatsapp") {
        window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, "_blank");
      } else if (channel === "instagram") {
        try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
        toast.success("Link copiato! Incollalo nella tua storia o bio Instagram");
        window.open("https://www.instagram.com/", "_blank");
      } else {
        try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
        toast.success("Link di condivisione copiato!", {
          action: { label: "Apri", onClick: () => window.open(url, "_blank") },
        });
      }
    } catch {
      toast.error("Impossibile creare il link di condivisione");
    }
  };

  const downloadImage = async () => {
    if (!proposal?.image_path) return;
    try {
      const r = await api.get(`/outfits/${proposal.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mylook-${(proposal.title || "look").replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Immagine scaricata");
    } catch {
      toast.error("Impossibile scaricare l'immagine");
    }
  };

  const swapItem = async (itemId) => {
    setSwapping(itemId);
    try {
      const r = await api.post(`/outfits/${proposal.id}/items/${itemId}/swap`);
      setProposal((p) => ({
        ...p,
        items: p.items.map((it) => (it.id === itemId ? r.data.item : it)),
        total_price: r.data.total_price,
        ...(r.data.image_path ? { image_path: r.data.image_path, image_url: r.data.image_url } : {}),
      }));
      toast.success("Capo sostituito e anteprima aggiornata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSwapping(null);
    }
  };

  const saveProposal = async () => {
    try {
      await api.post("/outfits/save", { proposal_id: proposal.id });
      setSaved(true);
      toast.success("Outfit salvato nel guardaroba");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updateSize = async (itemId, size) => {
    setProposal((p) => ({ ...p, items: p.items.map((it) => (it.id === itemId ? { ...it, size } : it)) }));
    try {
      await api.patch(`/outfits/${proposal.id}/items/${itemId}`, { size });
    } catch (err) {
      toast.error("Impossibile aggiornare la taglia");
    }
  };

  const hasAnyPhoto = Object.values(photos).some((p) => p?.uploaded);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      {/* Step 1 - Upload */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-2">
          <span className="font-mono text-xs text-[#a9772f]">01</span>
          <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Le tue foto</span>
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2e2015]">Carica la tua figura</h1>
        <p className="text-[#6f5c47] mt-2 max-w-2xl">
          Carica foto a figura intera da diverse angolazioni. Più angolazioni fornisci, più precisa sarà l'analisi della corporatura.
        </p>

        <div className="grid sm:grid-cols-3 gap-5 mt-8">
          {ANGLES.map((a) => (
            <PhotoCard
              key={a.key}
              angle={a}
              uploaded={photos[a.key]?.uploaded}
              preview={photos[a.key]?.preview}
              onSelect={selectPhoto}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-6">
          <Button
            data-testid="photo-submit-analysis-btn"
            onClick={runAnalysis}
            disabled={!hasAnyPhoto || analyzing}
            className="bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full px-6 h-11"
          >
            {analyzing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analisi in corso…</> : <><ScanLine className="mr-2 h-4 w-4" /> Analizza la corporatura</>}
          </Button>
          <span className="inline-flex items-center gap-1.5 text-xs text-[#8a7760]">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Massima riservatezza dei dati fisici
          </span>
        </div>
      </section>

      {/* Step 2 - Body profile */}
      <AnimatePresence>
        {profile && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs text-[#a9772f]">02</span>
              <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Analisi geometrica</span>
            </div>
            <h2 className="font-serif text-3xl font-semibold text-[#2e2015]">Il tuo profilo corporeo</h2>

            <div className="grid lg:grid-cols-12 gap-6 mt-8">
              <div className="lg:col-span-5 rounded-xl border border-[#1e5fe0]/25 bg-gradient-to-br from-white/80 to-[#c4d6e4] p-7">
                <div className="flex items-center gap-2 text-[#a9772f] text-xs font-mono uppercase tracking-widest mb-3">
                  <Ruler className="h-4 w-4" /> Corporatura
                </div>
                <div data-testid="body-shape-badge" className="font-serif text-4xl font-bold text-[#2f74f0] mb-4">
                  {profile.body_shape}
                </div>
                <p className="text-[#4d3a2a] leading-relaxed text-sm">{profile.body_shape_description}</p>
                <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-md bg-[#dce6ed]/50 border border-[#1e5fe0]/15 p-3">
                    <div className="text-xs text-[#8a7760] mb-1">Altezza stimata</div>
                    <div className="font-mono text-[#2e2015]">{profile.estimated_height}</div>
                  </div>
                  <div className="rounded-md bg-[#dce6ed]/50 border border-[#1e5fe0]/15 p-3">
                    <div className="text-xs text-[#8a7760] mb-1">Presentazione</div>
                    <div className="font-mono text-[#2e2015]">{profile.gender_presentation}</div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
                <div className="rounded-xl border border-[#1e5fe0]/15 bg-white/75 p-6">
                  <h3 className="font-serif text-lg text-[#2e2015] mb-3">Proporzioni</h3>
                  <ul className="space-y-2.5 text-sm text-[#4d3a2a]">
                    <li data-testid="body-metric-shoulders"><span className="text-[#a9772f]">Spalle/Fianchi: </span>{profile.shoulders_hips_ratio}</li>
                    <li data-testid="body-metric-waist"><span className="text-[#a9772f]">Punto vita: </span>{profile.waist_definition}</li>
                    <li><span className="text-[#a9772f]">Postura: </span>{profile.posture}</li>
                  </ul>
                  <h3 className="font-serif text-lg text-[#2e2015] mt-5 mb-2">Taglie consigliate</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.suggested_sizes && Object.entries(profile.suggested_sizes).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="border-[#1e5fe0]/30 text-[#1a4fc0] font-mono">
                        {k}: {v}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-[#1e5fe0]/15 bg-white/75 p-6">
                  <h3 className="font-serif text-lg text-[#2e2015] mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[#2f74f0]" /> Consigli dello stylist
                  </h3>
                  <ul className="space-y-2 text-sm text-[#4d3a2a]">
                    {(profile.styling_tips || []).map((t, i) => (
                      <li key={i} className="flex gap-2"><span className="text-[#2f74f0]">—</span> {t}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Step 3 - Generator controls */}
      {profile && (
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono text-xs text-[#a9772f]">03</span>
            <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Studio outfit</span>
          </div>
          <h2 className="font-serif text-3xl font-semibold text-[#2e2015]">Definisci lo stile e il budget</h2>

          <div className="rounded-xl border border-[#1e5fe0]/20 bg-white/80 backdrop-blur-xl p-6 sm:p-8 mt-8 grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-[#4d3a2a]">Tipo di outfit</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="outfit-style-select" className="bg-[#dce6ed]/60 border-[#1e5fe0]/20 text-[#2e2015]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                  {STYLES.map((s) => (
                    <SelectItem key={s} value={s} className="focus:bg-[#1e5fe0]/15 focus:text-[#1a4fc0]">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#4d3a2a]">Palette colori</Label>
              <Select value={color} onValueChange={setColor}>
                <SelectTrigger data-testid="outfit-color-select" className="bg-[#dce6ed]/60 border-[#1e5fe0]/20 text-[#2e2015]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                  {COLORS.map((c) => (
                    <SelectItem key={c} value={c} className="focus:bg-[#1e5fe0]/15 focus:text-[#1a4fc0]">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 md:col-span-2">
              <div className="flex items-center justify-between">
                <Label className="text-[#4d3a2a]">Budget totale</Label>
                <div className="flex items-center gap-2">
                  <span className="text-[#2f74f0] font-mono text-lg">€</span>
                  <Input
                    data-testid="outfit-budget-input"
                    type="number"
                    value={budget}
                    onChange={(e) => setBudget(Math.max(50, Number(e.target.value) || 0))}
                    className="w-28 bg-[#dce6ed]/60 border-[#1e5fe0]/20 text-[#2e2015] font-mono"
                  />
                </div>
              </div>
              <Slider
                data-testid="outfit-budget-slider"
                value={[budget]}
                onValueChange={(v) => setBudget(v[0])}
                min={50}
                max={5000}
                step={50}
                className="[&_[role=slider]]:bg-[#2f74f0] [&_[role=slider]]:border-[#2f74f0]"
              />
              <div className="flex justify-between text-xs text-[#8a7760] font-mono">
                <span>€50</span><span>€5000+</span>
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label className="text-[#4d3a2a]">Occasione / note per lo stylist</Label>
              <Textarea
                data-testid="outfit-occasion-input"
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                placeholder="Es. Cena di gala a Milano, aperitivo sul lago, colloquio di lavoro…"
                className="bg-[#dce6ed]/60 border-[#1e5fe0]/20 text-[#2e2015] min-h-[80px]"
              />
            </div>

            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-[#4d3a2a] whitespace-nowrap">Proposte:</Label>
                <div className="inline-flex rounded-full border border-[#1e5fe0]/25 bg-[#dce6ed]/60 p-1">
                  {[1, 2, 3].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`variant-count-${n}`}
                      onClick={() => setVariantCount(n)}
                      className={`h-8 w-9 rounded-full text-sm font-mono transition-colors ${
                        variantCount === n ? "bg-[#1e5fe0] text-white" : "text-[#1a4fc0] hover:bg-[#1e5fe0]/10"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <Button
                data-testid="outfit-generate-btn"
                onClick={handleGenerate}
                disabled={generating}
                className="w-full sm:w-auto bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full px-8 h-12 gold-glow"
              >
                {generating
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lo stylist sta creando…</>
                  : <><Wand2 className="mr-2 h-4 w-4" /> {variantCount > 1 ? `Genera ${variantCount} varianti` : "Genera outfit"}</>}
              </Button>
              {variantCount > 1 && (
                <span className="text-xs text-[#8a7760]">Consuma {variantCount} crediti-outfit (1 per variante)</span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Step 4 - Proposal */}
      <div id="proposal-anchor" />
      {generating && !proposal && (
        <div className="rounded-xl border border-[#1e5fe0]/20 h-64 shimmer grid place-items-center mb-16">
          <div className="text-center text-[#1a4fc0]">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
            <p className="font-serif text-lg">Composizione dell'outfit in corso…</p>
            <p className="text-xs text-[#6f5c47] mt-1">Analisi capi, prezzi e generazione dell'anteprima</p>
          </div>
        </div>
      )}

      {/* Variant chooser */}
      <AnimatePresence>
        {variants.length > 0 && !proposal && (
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs text-[#a9772f]">04</span>
              <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Le varianti</span>
            </div>
            <h2 className="font-serif text-3xl font-semibold text-[#2e2015]">Scegli la tua variante preferita</h2>
            <p className="text-[#6f5c47] mt-2 max-w-2xl">Tocca "Scegli" per vedere il dettaglio completo, le taglie e i link d'acquisto.</p>

            <div data-testid="variants-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {variants.map((v, i) => (
                <div
                  key={v.id}
                  data-testid={`variant-card-${i}`}
                  className="rounded-xl border border-[#1e5fe0]/20 bg-white/75 overflow-hidden flex flex-col"
                >
                  <div className="h-72 bg-[#dce6ed] overflow-hidden">
                    {v.image_url ? (
                      <img src={fileUrl(v.image_path)} alt={v.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="h-full grid place-items-center"><Shirt className="h-10 w-10 text-[#a9772f]/40" /></div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-serif text-lg text-[#2e2015]">{v.title}</h3>
                    <div className="flex items-center gap-2 mt-1 mb-3">
                      <Badge variant="outline" className="border-[#1e5fe0]/30 text-[#1a4fc0] text-xs">{v.style}</Badge>
                      <span className="text-[#2f74f0] font-mono text-sm">€{v.total_price}</span>
                    </div>
                    <p className="text-sm text-[#6f5c47] line-clamp-2">{v.summary}</p>
                    <ul className="mt-3 space-y-1 flex-1">
                      {(v.items || []).slice(0, 5).map((it) => (
                        <li key={it.id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-[#4d3a2a] truncate min-w-0">
                            <span className="text-[#a9772f]">{it.category}:</span> {it.name}
                          </span>
                          <span className="font-mono text-[#2f74f0] shrink-0">€{it.price}</span>
                        </li>
                      ))}
                      {(v.items || []).length > 5 && (
                        <li className="text-xs text-[#8a7760]">+{v.items.length - 5} altri capi</li>
                      )}
                    </ul>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-[#8a7760]">Totale</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${v.total_price <= v.budget ? "bg-emerald-500/15 text-emerald-700 border border-emerald-500/30" : "bg-red-500/15 text-red-600 border border-red-500/30"}`}>
                        €{v.total_price} / €{v.budget}
                      </span>
                    </div>
                    <Button
                      data-testid={`variant-choose-${i}`}
                      onClick={() => chooseVariant(v)}
                      className="mt-4 w-full bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold"
                    >
                      Scegli questa
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {proposal && (
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs text-[#a9772f]">04</span>
              <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">La proposta</span>
            </div>
            <h2 className="font-serif text-3xl font-semibold text-[#2e2015]">{proposal.title}</h2>
            <p className="text-[#6f5c47] mt-2 max-w-2xl">{proposal.summary}</p>

            <div className="grid lg:grid-cols-12 gap-6 mt-8">
              {/* Visualization */}
              <div className="lg:col-span-5">
                <div className="rounded-xl border border-[#1e5fe0]/25 bg-white/75 overflow-hidden">
                  {proposal.image_url ? (
                    <img
                      data-testid="outfit-visual-image"
                      src={fileUrl(proposal.image_path)}
                      alt={proposal.title}
                      className="w-full object-cover"
                    />
                  ) : (
                    <div className="h-96 grid place-items-center text-[#8a7760]">
                      <div className="text-center">
                        <Shirt className="h-10 w-10 mx-auto mb-2 text-[#a9772f]/50" />
                        <p className="text-sm px-6">Anteprima non disponibile. Carica una foto frontale per la visualizzazione.</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button
                    data-testid="outfit-accept-save-btn"
                    onClick={saveProposal}
                    disabled={saved}
                    className="flex-1 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold h-11"
                  >
                    {saved ? <><CheckCircle2 className="mr-2 h-4 w-4" /> Salvato</> : <><Save className="mr-2 h-4 w-4" /> Accetta e salva</>}
                  </Button>
                  <Button
                    data-testid="outfit-regenerate-btn"
                    onClick={generate}
                    variant="outline"
                    className="flex-1 border-[#1e5fe0]/30 text-[#1a4fc0] hover:bg-[#1e5fe0]/10 hover:text-[#3a2a1c] bg-transparent h-11"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" /> Rigenera
                  </Button>
                </div>
                <div className="flex gap-3 mt-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        data-testid="outfit-share-btn"
                        variant="outline"
                        className="flex-1 border-[#a9772f]/40 text-[#a9772f] hover:bg-[#a9772f]/10 bg-transparent h-11"
                      >
                        <Share2 className="mr-2 h-4 w-4" /> Condividi
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white border-[#1e5fe0]/20 text-[#2e2015]">
                      <DropdownMenuItem data-testid="share-whatsapp" onClick={() => shareProposal("whatsapp")} className="cursor-pointer focus:bg-[#1e5fe0]/10">
                        <MessageCircle className="mr-2 h-4 w-4 text-emerald-600" /> WhatsApp
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="share-instagram" onClick={() => shareProposal("instagram")} className="cursor-pointer focus:bg-[#1e5fe0]/10">
                        <Instagram className="mr-2 h-4 w-4 text-pink-600" /> Instagram
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="share-copy" onClick={() => shareProposal("copy")} className="cursor-pointer focus:bg-[#1e5fe0]/10">
                        <Link2 className="mr-2 h-4 w-4 text-[#1e5fe0]" /> Copia link
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    data-testid="outfit-download-btn"
                    onClick={downloadImage}
                    variant="outline"
                    className="flex-1 border-[#1e5fe0]/30 text-[#1a4fc0] hover:bg-[#1e5fe0]/10 bg-transparent h-11"
                  >
                    <Download className="mr-2 h-4 w-4" /> Salva immagine
                  </Button>
                </div>
              </div>

              {/* Items + cart */}
              <div className="lg:col-span-7">
                <div className="rounded-xl border border-[#1e5fe0]/15 bg-white/75 p-5 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-[#8a7760]">Totale outfit</div>
                      <div data-testid="cart-total-price" className="font-serif text-2xl text-[#2f74f0]">€ {proposal.total_price}</div>
                    </div>
                    <Badge
                      data-testid="cart-budget-status"
                      className={proposal.total_price <= proposal.budget ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"}
                    >
                      {proposal.total_price <= proposal.budget ? "Entro il budget" : "Oltre il budget"} · €{proposal.budget}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-3">
                  {proposal.items.map((it) => (
                    <div
                      key={it.id}
                      data-testid={`item-card-${it.id}`}
                      className="rounded-lg border border-[#1e5fe0]/15 bg-[#dce6ed]/40 p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                    >
                      {it.image ? (
                        <img src={it.image} alt={it.name} className="h-16 w-16 rounded-md object-cover border border-[#1e5fe0]/20 shrink-0 bg-white" />
                      ) : (
                        <div className="h-16 w-16 rounded-md border border-[#1e5fe0]/20 shrink-0 bg-[#dce6ed]/60 grid place-items-center text-[#a9772f]">
                          <Shirt className="h-6 w-6" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">{it.category}</span>
                          {it.verified ? (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 border border-emerald-500/30">Prodotto verificato</span>
                          ) : (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#a9772f]/15 text-[#a9772f] border border-[#a9772f]/30">Ricerca negozio</span>
                          )}
                          {it.substituted && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#1e5fe0]/10 text-[#1e5fe0] border border-[#1e5fe0]/30">Alternativa</span>
                          )}
                        </div>
                        <div className="font-medium text-[#2e2015] truncate">{it.name}</div>
                        <div className="text-sm text-[#8a7760]">{it.brand}{it.color ? ` · ${it.color}` : ""} · {it.store}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div>
                          <Label className="text-[10px] text-[#8a7760] uppercase">Taglia</Label>
                          <Input
                            data-testid={`item-size-input-${it.id}`}
                            value={it.size}
                            onChange={(e) => updateSize(it.id, e.target.value)}
                            className="h-9 w-24 bg-white border-[#1e5fe0]/20 text-[#2e2015] font-mono text-sm"
                          />
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-[#2f74f0]">€{it.price}</div>
                          <a
                            data-testid={`store-link-${it.id}`}
                            href={it.buy_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#6f5c47] hover:text-[#1e5fe0] mt-1"
                          >
                            Acquista <ExternalLink className="h-3 w-3" />
                          </a>
                          <button
                            type="button"
                            data-testid={`item-swap-${it.id}`}
                            onClick={() => swapItem(it.id)}
                            disabled={swapping === it.id}
                            className="flex items-center gap-1 text-xs text-[#a9772f] hover:text-[#8a5f1f] mt-1 ml-auto disabled:opacity-60"
                          >
                            {swapping === it.id
                              ? <><Loader2 className="h-3 w-3 animate-spin" /> Cambio…</>
                              : <><Repeat className="h-3 w-3" /> Cambia capo</>}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-lg border border-[#1e5fe0]/20 bg-[#1e5fe0]/5 p-4 flex items-start gap-3">
                  <ShoppingBag className="h-5 w-5 text-[#2f74f0] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#6f5c47]">
                    I capi <strong className="text-emerald-700">"Prodotto verificato"</strong> aprono direttamente la scheda del prodotto reale sul negozio, al prezzo indicato. Le taglie sono suggerite dall'IA e modificabili prima dell'acquisto.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}
